"use client";

import { type ReactNode, useEffect, useRef, useState } from "react";

/** Scroll-triggered entrance: children fade/slide in when the wrapper enters
 * the viewport. `index` staggers siblings via the --i custom property. */
export default function Reveal({
  children,
  index = 0,
  className = "",
}: {
  children: ReactNode;
  index?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`ld-reveal ${inView ? "is-in" : ""} ${className}`}
      style={{ "--i": index } as React.CSSProperties}
    >
      {children}
    </div>
  );
}
